import { useState } from 'react';
import { purchaseOrderFormInitialValues } from '../../data/purchaseOrderConstants';
import { updatePurchaseOrder } from '../../services/purchaseOrderService';
import {
  buildPurchaseOrderEditValues,
  getSupplierSacksMark,
  normalizePurchaseOrderPayload,
  validatePurchaseOrderForm,
} from './purchaseOrderHelpers';

function usePurchaseOrderEdit({
  supplierOptions,
  setPurchaseOrders,
  setSuccessMessage,
}) {
  const [editingPurchaseOrder, setEditingPurchaseOrder] = useState(null);
  const [editValues, setEditValues] = useState(purchaseOrderFormInitialValues);
  const [editErrors, setEditErrors] = useState({});
  const [editError, setEditError] = useState('');
  const [isEditSaving, setIsEditSaving] = useState(false);

  function openEditModal(purchaseOrder) {
    setEditingPurchaseOrder(purchaseOrder);
    setEditValues(buildPurchaseOrderEditValues(purchaseOrder));
    setEditErrors({});
    setEditError('');
    setSuccessMessage('');
  }

  function closeEditModal() {
    setEditingPurchaseOrder(null);
    setEditValues(purchaseOrderFormInitialValues);
    setEditErrors({});
    setEditError('');
  }

  function handleEditChange(event) {
    const { name, value } = event.target;

    setEditValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      ...(name === 'supplierId'
        ? { sacksMark: getSupplierSacksMark(value, supplierOptions) }
        : {}),
    }));
    setEditErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'supplierId' ? { sacksMark: '' } : {}),
    }));
    setEditError('');
    setSuccessMessage('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    if (!editingPurchaseOrder) {
      return false;
    }

    if (editingPurchaseOrder.balancingReadyAt) {
      setEditError('This P.O. is finalized for Supplier Balancing. Changes are locked.');
      return false;
    }

    const nextErrors = validatePurchaseOrderForm(editValues, {
      requireWarehouseLocation: false,
      requireDeliveryDate: false,
      requireSeaweedType: false,
    });

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsEditSaving(true);
    setEditError('');

    try {
      const payload = normalizePurchaseOrderPayload({
        ...editValues,
        status: editingPurchaseOrder.status,
      });
      const updatedPurchaseOrder = await updatePurchaseOrder(
        editingPurchaseOrder.id,
        payload
      );

      setPurchaseOrders((currentOrders) =>
        currentOrders.map((purchaseOrder) =>
          purchaseOrder.id === editingPurchaseOrder.id ? updatedPurchaseOrder : purchaseOrder
        )
      );
      closeEditModal();
      setSuccessMessage('Purchase order updated successfully.');
      return true;
    } catch (error) {
      setEditError(error.message);
      return false;
    } finally {
      setIsEditSaving(false);
    }
  }

  return {
    editingPurchaseOrder,
    editValues,
    editErrors,
    editError,
    isEditSaving,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
  };
}

export default usePurchaseOrderEdit;
